<template>
  <b-modal
    v-model="isVisible"
    title="Создать родительский аккаунт"
    size="lg"
    @hidden="resetForm"
    @ok.prevent="handleSubmit"
    ok-title="Создать"
    cancel-title="Отмена"
    :ok-disabled="!isFormValid"
  >
    <b-form @submit.prevent="handleSubmit">
      <div class="row">
        <div class="col-md-6">
          <b-form-group
            label="Имя"
            label-for="parent-name"
          >
            <b-form-input
              id="parent-name"
              v-model="form.name"
              placeholder="Введите имя родителя"
              required
              :state="getValidationState('name')"
            />
            <b-form-invalid-feedback>
              {{ getValidationMessage('name') }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            label="Email"
            label-for="parent-email"
          >
            <b-form-input
              id="parent-email"
              v-model="form.email"
              type="email"
              placeholder="Введите email"
              required
              :state="getValidationState('email')"
            />
            <b-form-invalid-feedback>
              {{ getValidationMessage('email') }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            label="Пароль"
            label-for="parent-password"
          >
            <b-input-group>
              <b-form-input
                id="parent-password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Введите пароль (мин. 6 символов)"
                required
                :state="getValidationState('password')"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  @click="showPassword = !showPassword"
                >
                  <feather-icon :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'" size="16" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback>
              {{ getValidationMessage('password') }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>

        <div class="col-md-6">
          <b-form-group
            label="Телефон (необязательно)"
            label-for="parent-phone"
          >
            <b-form-input
              id="parent-phone"
              v-model="form.phone"
              placeholder="Введите номер телефона"
              :state="getValidationState('phone')"
            />
            <b-form-invalid-feedback>
              {{ getValidationMessage('phone') }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            label="Местоположение (необязательно)"
            label-for="parent-location"
          >
            <b-form-input
              id="parent-location"
              v-model="form.location"
              placeholder="Введите город/регион"
              :state="getValidationState('location')"
            />
          </b-form-group>

          <b-form-group
            label="Заметки (необязательно)"
            label-for="parent-notes"
          >
            <b-form-textarea
              id="parent-notes"
              v-model="form.notes"
              placeholder="Дополнительная информация..."
              rows="3"
            />
          </b-form-group>
        </div>
      </div>
    </b-form>

    <!-- Password Strength Indicator -->
    <div v-if="form.password" class="mt-3">
      <label class="form-label">Надежность пароля:</label>
      <b-progress
        :value="passwordStrength.score * 25"
        :variant="passwordStrength.variant"
        class="mb-2"
      />
      <small :class="`text-${passwordStrength.variant}`">
        {{ passwordStrength.message }}
      </small>
    </div>

    <!-- Preview -->
    <div v-if="isPreviewVisible" class="mt-4">
      <hr>
      <h6>Предварительный просмотр:</h6>
      <div class="border rounded p-3 bg-light">
        <div class="row">
          <div class="col-md-6">
            <p><strong>Имя:</strong> {{ form.name }}</p>
            <p><strong>Email:</strong> {{ form.email }}</p>
            <p><strong>Телефон:</strong> {{ form.phone || 'Не указан' }}</p>
          </div>
          <div class="col-md-6">
            <p><strong>Местоположение:</strong> {{ form.location || 'Не указано' }}</p>
            <p><strong>Заметки:</strong> {{ form.notes || 'Нет заметок' }}</p>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormInvalidFeedback,
  BButton,
  BInputGroup,
  BInputGroupAppend,
  BProgress
} from 'bootstrap-vue'
import ParentalControlAPI from '@/api/parentalControl'

export default {
  name: 'CreateParentModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BProgress
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isVisible: this.value,
      showPassword: false,
      isSubmitting: false,
      validationErrors: {},
      form: {
        name: '',
        email: '',
        password: '',
        phone: '',
        location: '',
        notes: ''
      }
    }
  },
  computed: {
    isFormValid() {
      return this.form.name &&
             this.form.email &&
             this.form.password &&
             this.form.password.length >= 6 &&
             this.isValidEmail(this.form.email)
    },
    isPreviewVisible() {
      return this.form.name && this.form.email
    },
    passwordStrength() {
      const password = this.form.password
      if (!password) return { score: 0, variant: 'danger', message: 'Введите пароль' }

      let score = 0
      let message = ''
      let variant = 'danger'

      if (password.length >= 6) score++
      if (password.length >= 8) score++
      if (/[A-Z]/.test(password)) score++
      if (/[0-9]/.test(password)) score++
      if (/[^A-Za-z0-9]/.test(password)) score++

      switch (score) {
        case 0:
        case 1:
          message = 'Очень слабый'
          variant = 'danger'
          break
        case 2:
          message = 'Слабый'
          variant = 'warning'
          break
        case 3:
          message = 'Средний'
          variant = 'info'
          break
        case 4:
          message = 'Хороший'
          variant = 'primary'
          break
        case 5:
          message = 'Отличный'
          variant = 'success'
          break
      }

      return { score, variant, message }
    }
  },
  watch: {
    value(newVal) {
      this.isVisible = newVal
    },
    isVisible(newVal) {
      this.$emit('input', newVal)
    }
  },
  methods: {
    async handleSubmit() {
      if (!this.isFormValid) return

      this.isSubmitting = true
      this.validationErrors = {}

      try {
        const response = await ParentalControlAPI.createParent(this.form)

        this.$bvToast.toast('Родительский аккаунт успешно создан', {
          title: 'Успех',
          variant: 'success',
          solid: true,
        })

        this.$emit('parent-created', response.data.parent)
        this.resetForm()
        this.isVisible = false

      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.validationErrors = error.response.data.errors
        } else {
          this.$bvToast.toast('Ошибка при создании аккаунта', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true,
          })
        }
      } finally {
        this.isSubmitting = false
      }
    },
    resetForm() {
      this.form = {
        name: '',
        email: '',
        password: '',
        phone: '',
        location: '',
        notes: ''
      }
      this.validationErrors = {}
      this.showPassword = false
    },
    getValidationState(field) {
      if (this.validationErrors[field]) return false
      if (!this.form[field]) return null

      // Specific validation for email
      if (field === 'email' && this.form.email) {
        return this.isValidEmail(this.form.email)
      }

      // Specific validation for password
      if (field === 'password' && this.form.password) {
        return this.form.password.length >= 6
      }

      return this.form[field] ? true : null
    },
    getValidationMessage(field) {
      if (this.validationErrors[field]) {
        return Array.isArray(this.validationErrors[field])
          ? this.validationErrors[field][0]
          : this.validationErrors[field]
      }

      switch (field) {
        case 'name':
          return 'Пожалуйста, введите имя родителя'
        case 'email':
          return 'Пожалуйста, введите корректный email адрес'
        case 'password':
          return 'Пароль должен содержать минимум 6 символов'
        case 'phone':
          return 'Неверный формат номера телефона'
        default:
          return 'Это поле обязательно для заполнения'
      }
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    }
  }
}
</script>

<style lang="scss" scoped>
.progress {
  height: 6px;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.border {
  border: 1px solid #dee2e6 !important;
}

.dark-layout {
  .bg-light {
    background-color: #404656 !important;
    color: #d0d2d6;
  }

  .border {
    border-color: #555 !important;
  }
}
</style>